import { useMediaQuery, useSkyTheme } from "@skypointcloud/skypoint-ui";
import { helpThemeCookie } from "@shared/helpTheme";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Site-wide theme (GUIDELINES.md §3.3, UX-2). `preference` is what the user
 * picked; `resolvedTheme` is what is actually painted (`system` resolves
 * through the OS setting).
 *
 * One toggle must flip BOTH class systems that key off <html>:
 *   1. Tailwind's `.dark`    — this app's own `dark:` utilities;
 *   2. skypoint-ui's `.sky-dark` / `.sky-light` — written by SkyProvider, which
 *      is why `setMode` is called alongside rather than instead.
 *
 * IMPORTANT: STORAGE_KEY and the light/dark resolve logic here MUST stay
 * byte-identical to the pre-paint guard in `client/index.html`. Change one,
 * change the other, or the first frame after a reload paints the wrong theme.
 */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "skydata-theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** The stored choice, or light. Exported so SkyProvider can seed `defaultMode`. */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    /* localStorage unavailable (private mode) — fall through */
  }
  return "light";
}

function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference !== "system") return preference;
  return systemPrefersDark ? "dark" : "light";
}

/**
 * B-66: hand the choice to the user guide at `/help`, which is static pages with no script and
 * so cannot read `localStorage`. A cookie scoped to `/help` carries the preference (not the
 * resolved theme: System stays System there too); the server marks each page from it.
 */
function shareWithHelp(preference: ThemePreference): void {
  try {
    document.cookie = helpThemeCookie(preference, window.location.protocol === "https:");
  } catch {
    /* cookies blocked — the guide follows the device instead */
  }
}

/** The single place that writes the DOM. Mirrors the index.html guard. */
function applyTheme(resolved: ResolvedTheme): void {
  const el = document.documentElement;
  el.classList.toggle("dark", resolved === "dark");
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#1a1a1a" : "#242424");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { setMode } = useSkyTheme();
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [preference, setPref] = useState<ThemePreference>(readStoredPreference);

  const resolvedTheme = resolveTheme(preference, systemPrefersDark);

  useEffect(() => {
    applyTheme(resolvedTheme);
    shareWithHelp(preference);
    // Hand the design system its own namespace ('system' is a valid mode there).
    setMode(preference);
  }, [resolvedTheme, preference, setMode]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPref(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* private mode — the choice simply won't survive a reload */
    }
  }, []);

  const value = useMemo(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
}
